import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { confirmIfFarDate, confirmIfFarCompetence } from "@/lib/dateGuard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";
import ErrorBoundary from "@/components/ErrorBoundary";

import CaseHeader from "./caseDetail/CaseHeader";
import ResumoTab from "./caseDetail/ResumoTab";
import PacienteTab from "./caseDetail/PacienteTab";
import DadosTab from "./caseDetail/DadosTab";
import ProfissionaisTab from "./caseDetail/ProfissionaisTab";
import AssinadosTab from "./caseDetail/AssinadosTab";
import DivergenciasTab from "./caseDetail/DivergenciasTab";
import FinanceiroTab from "./caseDetail/FinanceiroTab";
import HistoricoTab from "./caseDetail/HistoricoTab";
import { pendingDiscrepancyCount } from "./caseDetail/utils";

/** Orchestrator for /casos/:id — owns data fetching and mutations; delegates
 * rendering to focused tab components under /caseDetail/. */
export default function CaseDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "admin_master";

  const [cs, setCs] = useState(null);
  const [surgeons, setSurgeons] = useState([]);
  const [anesths, setAnesths] = useState([]);
  const [iteches, setIteches] = useState([]);
  const [procs, setProcs] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [edit, setEdit] = useState({});
  const [changeReason, setChangeReason] = useState("");
  const [uploading, setUploading] = useState({});
  const [analyses, setAnalyses] = useState([]);
  const [analyzing, setAnalyzing] = useState({});

  async function loadAnalyses() {
    try {
      const r = await api.get(`/ai/cases/${id}/analyses`);
      setAnalyses(r.data || []);
    } catch (e) { /* tab will be empty */ }
  }

  async function load() {
    try {
      const r = await api.get(`/cases/${id}`);
      setCs(r.data);
      setEdit(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  useEffect(() => {
    load();
    loadAnalyses();
    api.get("/surgeons").then((r) => setSurgeons(r.data));
    api.get("/anesthesiologists").then((r) => setAnesths(r.data));
    api.get("/instrument-technicians").then((r) => setIteches(r.data));
    api.get("/procedures").then((r) => setProcs(r.data));
    api.get("/cases/statuses").then((r) => setStatuses(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function saveEdit(patch, extraReason) {
    try {
      const payload = { ...patch };
      if (extraReason || changeReason) payload.change_reason = extraReason || changeReason;
      const r = await api.patch(`/cases/${id}`, payload);
      setCs({ ...cs, ...r.data });
      setEdit({ ...edit, ...r.data });
      setChangeReason("");
      toast.success("Caso atualizado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function savePayload() {
    const diff = {};
    ["surgery_date", "surgery_time", "estimated_duration", "bed", "procedure_id", "procedure_name",
     "diagnosis", "surgeon_id", "assistant_surgeon_id", "assistant_surgeon_2_id", "anesthesiologist_id",
     "instrument_technician_id", "status", "notes", "billing_competence", "pro_bono"].forEach((f) => {
      if (edit[f] !== cs[f]) diff[f] = edit[f];
    });
    const a = (edit.procedure_ids || []).join("|");
    const b = (cs.procedure_ids || []).join("|");
    if (a !== b) diff.procedure_ids = edit.procedure_ids || [];
    if (Object.keys(diff).length === 0) { toast.info("Sem alterações"); return; }
    if ("surgery_date" in diff && !(await confirmIfFarDate(diff.surgery_date, "data da cirurgia"))) return;
    if ("billing_competence" in diff && !(await confirmIfFarCompetence(diff.billing_competence, "competência de faturamento"))) return;
    await saveEdit(diff);
  }

  function addProcedure(pid) {
    if (!pid) return;
    const current = edit.procedure_ids || (edit.procedure_id ? [edit.procedure_id] : []);
    if (current.includes(pid)) { toast.info("Procedimento já adicionado"); return; }
    const next = [...current, pid];
    const primary = procs.find((x) => x.id === next[0]);
    setEdit({
      ...edit,
      procedure_ids: next,
      procedure_id: next[0],
      procedure_name: primary?.name || edit.procedure_name,
      diagnosis: edit.diagnosis || primary?.default_diagnosis || "",
    });
  }

  function removeProcedure(pid) {
    const current = edit.procedure_ids || (edit.procedure_id ? [edit.procedure_id] : []);
    const next = current.filter((x) => x !== pid);
    const primary = next.length ? procs.find((x) => x.id === next[0]) : null;
    setEdit({
      ...edit,
      procedure_ids: next,
      procedure_id: next[0] || null,
      procedure_name: primary?.name || (next.length === 0 ? "" : edit.procedure_name),
    });
  }

  async function saveFinancials() { await saveEdit({ financials: edit.financials }); }

  function openSigned(doc) {
    const token = localStorage.getItem("bv_token");
    fetch(`${API.replace('/api','')}${doc.file_url}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => window.open(URL.createObjectURL(b)));
  }

  async function uploadMultipleSigned(files) {
    if (!files || files.length === 0) return;
    setUploading({ ...uploading, assinado: true });
    let ok = 0, fail = 0;
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("doc_type", "assinado");
        fd.append("file", file);
        await api.post(`/cases/${id}/documents/signed`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) {
        fail += 1;
        toast.error(`${file.name}: ${formatApiError(e.response?.data?.detail)}`);
      }
    }
    if (ok > 0) toast.success(`${ok} documento(s) assinado(s) enviado(s).`);
    if (fail === 0 && ok === 0) toast.error("Nenhum arquivo selecionado.");
    setUploading({ ...uploading, assinado: false });
    load();
  }

  async function deleteSigned(docId, label) {
    if (!(await confirm({ description: `Apagar o documento "${label}"?\n\nEsta ação NÃO pode ser desfeita (mas você pode enviar um novo).` }))) return;
    try {
      await api.delete(`/cases/${id}/documents/signed/${docId}`);
      toast.success("Documento apagado.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function analyzeWithAI(doc) {
    if (analyzing[doc.id]) return;
    setAnalyzing((prev) => ({ ...prev, [doc.id]: true }));
    try {
      const r = await api.post(`/ai/cases/${id}/analyze-signed/${doc.id}`);
      const count = (r.data.discrepancies || []).length;
      if (count === 0) toast.success("Análise concluída — nenhuma divergência encontrada.");
      else toast.warning(`Análise concluída — ${count} divergência(s) detectada(s).`);
      await loadAnalyses();
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAnalyzing((prev) => ({ ...prev, [doc.id]: false })); }
  }

  async function resolveDiscrepancy(analysisId, field, action) {
    try {
      const r = await api.post(`/ai/analyses/${analysisId}/resolve`, { field, action });
      if (action === "apply") {
        toast.success(r.data.applied_to === "patient" ? "Aplicado no cadastro do paciente." : "Aplicado no caso.");
      } else {
        toast.success("Divergência descartada (sistema mantido).");
      }
      await loadAnalyses();
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function deleteAnalysis(aid) {
    if (!(await confirm({ description: "Descartar esta análise?" }))) return;
    try {
      await api.delete(`/ai/analyses/${aid}`);
      await loadAnalyses();
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function deleteCase() {
    if (!isAdmin) return;
    const code = cs?.case_code || "este caso";
    if (!(await confirm({ description: `Excluir definitivamente o caso ${code}?\n\n` +
      `Isto vai:\n• Remover o caso\n• Remover a linha vinculada do mapa filtrado\n` +
      `• Apagar análises de IA, divergências e documentos atrelados\n\n` +
      `Produção, Faturamento e Financeiro vão refletir essa exclusão automaticamente.\n\n` +
      `Esta ação não pode ser desfeita. Continuar?` }))) return;
    try {
      const r = await api.delete(`/cases/${id}`);
      const d = r.data || {};
      toast.success(`Caso ${code} excluído.` + (d.row_removed_from_map ? " Linha do mapa removida." : ""));
      nav("/casos");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Falha ao excluir caso"); }
  }

  if (!cs) return <div className="text-slate-500">Carregando...</div>;

  const pending = pendingDiscrepancyCount(analyses);

  return (
    <div className="space-y-6" data-testid="case-detail">
      <Button variant="ghost" size="sm" onClick={() => nav(-1)}>
        <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
      </Button>

      <CaseHeader cs={cs} analyses={analyses} isAdmin={isAdmin} onDelete={deleteCase} />

      <Tabs defaultValue="resumo">
        <TabsList className="bg-white border border-slate-200 overflow-x-auto flex-wrap">
          <TabsTrigger value="resumo" data-testid="tab-resumo">Resumo</TabsTrigger>
          <TabsTrigger value="paciente" data-testid="tab-paciente">Paciente</TabsTrigger>
          <TabsTrigger value="dados" data-testid="tab-dados">Dados do Caso</TabsTrigger>
          <TabsTrigger value="profissionais" data-testid="tab-profissionais">Profissionais</TabsTrigger>
          <TabsTrigger value="assinados" data-testid="tab-assinados">Assinados</TabsTrigger>
          <TabsTrigger value="divergencias" data-testid="tab-divergencias">
            Divergências
            {pending > 0 && <span className="ml-1.5 text-[10px] bg-amber-500 text-white rounded-full px-1.5 py-0.5">{pending}</span>}
          </TabsTrigger>
          <TabsTrigger value="financeiro" data-testid="tab-financeiro">Financeiro</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico">Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="resumo">
          <ErrorBoundary inline label="Resumo indisponível" testid="resumo-error">
            <ResumoTab cs={cs} />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="paciente">
          <ErrorBoundary inline label="Dados do paciente indisponíveis" testid="paciente-error">
            <PacienteTab patient={cs.patient} />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="dados">
          <ErrorBoundary inline label="Editor de dados indisponível" testid="dados-error">
            <DadosTab
              cs={cs} edit={edit} setEdit={setEdit} procs={procs} statuses={statuses}
              changeReason={changeReason} setChangeReason={setChangeReason}
              addProcedure={addProcedure} removeProcedure={removeProcedure}
              savePayload={savePayload}
            />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="profissionais">
          <ErrorBoundary inline label="Editor de profissionais indisponível" testid="profissionais-error">
            <ProfissionaisTab
              edit={edit} setEdit={setEdit}
              surgeons={surgeons} anesths={anesths} iteches={iteches}
              changeReason={changeReason} setChangeReason={setChangeReason}
              savePayload={savePayload}
            />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="assinados">
          <ErrorBoundary inline label="Lista de assinados indisponível" testid="assinados-error">
            <AssinadosTab
              cs={cs} uploading={uploading} analyzing={analyzing}
              onUploadMultiple={uploadMultipleSigned}
              onOpen={openSigned}
              onAnalyze={analyzeWithAI}
              onDelete={deleteSigned}
            />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="divergencias">
          <ErrorBoundary inline label="Lista de divergências indisponível" testid="divergencias-error">
            <DivergenciasTab
              analyses={analyses}
              onResolve={resolveDiscrepancy}
              onDeleteAnalysis={deleteAnalysis}
            />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="financeiro">
          <ErrorBoundary inline label="Editor financeiro indisponível" testid="financeiro-error">
            <FinanceiroTab edit={edit} setEdit={setEdit} onSave={saveFinancials} />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="historico">
          <ErrorBoundary inline label="Histórico indisponível" testid="historico-error">
            <HistoricoTab cs={cs} />
          </ErrorBoundary>
        </TabsContent>
      </Tabs>
    </div>
  );
}
